import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _, { bind } from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { fromLegacyTimeZone, toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { MessageBar } from '@fluentui/react';
import { HelpLink } from '@bingads-webui-react/help-link';
import { GridContext, ContextualDetailsList } from '@bingads-webui-grid/grid-context';
import { ToolbarContainer } from '@bingads-webui-campaign-react/toolbar-container';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { FilterEntity } from '@bingads-webui/filter-entity';
import { FilterService } from '@bingads-webui/filter-service';
import { FilterViewManagerWithGlobalFilterContext } from '@bingads-webui-campaign-react/filter-view-manager';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { ManageBrandListPanel } from '@bingads-webui-campaign-react/brand-lists-components';
import { helpbuttonStyle, convertToLocalStyle } from '@bingads-webui-campaign-react/common-styles';
import { getRoutePathById, withSiteMap, siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { Redirect } from '@bingads-webui-campaign-react/router';
import { getBrandsUnderBrandListEdm, updateBrandListFromProp, getBrandListById, getViewModelBulkUpsertErrors, GENERIC_ERROR_MESSAGE } from '@bingads-webui-campaign-react/brand-lists-data';
import { useCampaignGridDataSource } from '@bingads-webui-grid/campaign-data-source';
import { buildColumns } from './configs/columns-configs';
import { getToolbarConfig } from './configs/toolbar-config';
import { getFilterConfig } from './configs/filter-config';
import { DeleteDialog } from '../components/delete-dialog';
import { StatusCallout } from './status-with-callout';

const ToolbarShownItem = {
  Toolbar: 'Toolbar',
  FilterView: 'FilterView',
};

const getStyles = (_components, { palette }) => ({
  BrandListsDetailsGrid: {
    '& .title': {
      marginTop: 0,
      fontSize: 26,
      fontWeight: 600,
      lineHeight: '27px',
    },
    '& .description': {
      padding: '8px 0',
    },
    '& .review-delay-message-bar': {
      backgroundColor: palette.themeLighterAlt,
      marginBottom: 16,
    },
    '& .toolbar-container .toolbar': {
      '& .container-fluid': {
        paddingLeft: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        '& .navbar-nav li': {
          marginRight: 16,
        },
      },
    },
    '& .toolbar-container .toolbar-edit .container-fluid .btn-link': {
      padding: '0 8px',
    },
    '& .rejected-brand': {
      color: palette.neutralLight,
    },
    ...convertToLocalStyle(helpbuttonStyle({ palette })),
  },
});

const BrandListsDetailsGridInner = ({ siteMap, permissions }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const preferenceService = useContext(PreferenceContext);
  const { scope } = useContext(ScopeContext);
  const {
    values: {
      cid,
      aid,
      brandListId,
    },
  } = scope;
  const serverContext = useContext(ServerContext);
  const {
    CurrentAccount: {
      TimeZone,
    },
  } = serverContext;
  const timeZone = useMemo(() => toIANATimeZone(fromLegacyTimeZone(TimeZone)), [TimeZone]);

  const [listName, setListName] = useState('');
  const { state, dispatch } = useContext(GridContext);

  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedItemStatus, setSelectedItemStatus] = useState('');
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const showCallout = useCallback(({ itemId, itemStatus }) => {
    setSelectedItemId(itemId);
    setSelectedItemStatus(itemStatus);
    setIsCalloutVisible(true);
  }, []);
  const hideCallout = useCallback(() => setIsCalloutVisible(false), []);

  useEffect(() => {
    async function fetchData() {
      try {
        const brandList = await getBrandListById({ cid, aid, brandListId });
        setListName(brandList ? brandList.Name : '');
      } catch (error) {
        notificationApi.showWarning(i18n.getString(GENERIC_ERROR_MESSAGE));
      }
    }
    fetchData();
  }, [aid, brandListId, cid, i18n]);

  const reloadData = useCallback(() => {
    dispatch({ type: 'REFRESH_GRID_DATA' });
  }, [dispatch]);

  useCampaignGridDataSource({
    edm: getBrandsUnderBrandListEdm({
      cid,
      aid,
      brandListId,
    }),
    odataParams: {
      query: {
        select: undefined,
      },
    },
    onError: () => {
      notificationApi.showWarning(i18n.getString(GENERIC_ERROR_MESSAGE), {
        autoDismissTime: 5000,
      });
    },
  });

  useEffect(() => {
    const newColumns = buildColumns({ i18n, showCallout, hideCallout, timeZone });
    dispatch({ type: 'UPDATE_SELECTED_COLUMNS', payload: newColumns });
  }, [dispatch, i18n, showCallout, hideCallout, timeZone]);

  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const onOpenEditPanel = () => setIsEditPanelOpen(true);
  const onCloseEditPanel = () => setIsEditPanelOpen(false);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const onOpenDeleteDialog = () => setIsDeleteDialogOpen(true);
  const onCloseDeleteDialog = () => setIsDeleteDialogOpen(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleDelete = useCallback(async () => {
    const deletePayload = {
      BrandItems: _.map(state.selectedItems, (brand) => {
        if (brand && brand.BrandId) {
          return {
            Id: brand.BrandId,
            Status: 'Deleted',
            ParentId: brandListId,
          };
        }
        return null;
      }),
    };

    setDeleting(true);
    try {
      const deleteCall = updateBrandListFromProp({ cid, aid });
      await deleteCall(deletePayload);
      state.selection.setAllSelected(false);
      onCloseDeleteDialog();
      reloadData();
    } catch (error) {
      const serverErrors = getViewModelBulkUpsertErrors(i18n, error);
      setDeleteError(serverErrors[0].message);
    } finally {
      setDeleting(false);
    }
  }, [aid, brandListId, cid, i18n, reloadData, state.selectedItems, state.selection]);

  const toolbarConfig = useMemo(() => getToolbarConfig({
    i18n,
    selectedItems: state.selectedItems,
    handleEdit: () => {
      onOpenEditPanel();
    },
    handleDelete: () => {
      onOpenDeleteDialog();
    },
  }), [i18n, state.selectedItems]);

  const [toolbarShownItem, setToolbarShownItem] = useState(ToolbarShownItem.Toolbar);

  function renderToolbarContainer() {
    return (
      <ToolbarContainer
        i18n={i18n}
        forceHideToolbar={toolbarShownItem !== ToolbarShownItem.Toolbar}
        showToolbarUnselected={state.selectedItems.length === 0}
        showToolbarSelected={state.selectedItems.length > 0}
        toolbarSelectedConfig={toolbarConfig.toolbarSelectedConfig}
        toolbarUnselectedConfig={toolbarConfig.toolbarUnselectedConfig}
        onClear={() => state.selection.setAllSelected(false)}
        selectionCount={state.selectedItems.length}
      />
    );
  }

  const toolbarEditor = useMemo(() => getToolbarEditor({ toolbarConfig }), [toolbarConfig]);
  const filterConfig = useMemo(() => getFilterConfig({ i18n, preferenceService }), [i18n, preferenceService]);
  const resetToolbarVisibility = useCallback(({ forceHideToolbar }) => {
    if (forceHideToolbar) {
      setToolbarShownItem(ToolbarShownItem.FilterView);
    } else {
      setToolbarShownItem(ToolbarShownItem.Toolbar);
    }
  }, []);

  const filterService = useMemo(() => {
    const newFilterService = new FilterService(filterConfig);

    newFilterService.on('filter-state-changed', () => {
      const filterEntity = newFilterService.getCombinedFilter();
      const query = filterEntity ? FilterEntity.toOdata(filterEntity.query, filterEntity.odataOptions) : undefined;
      dispatch({ type: 'SET_EDITABLE_FILTER', payload: query });
    });

    return newFilterService;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  const renderFilterViewManager = useCallback(() => {
    const toolbarEditorUpdate = bind(toolbarEditor.update, toolbarEditor);

    return (
      <div>
        <FilterViewManagerWithGlobalFilterContext
          filterService={filterService}
          updateToolbar={toolbarEditorUpdate}
          resetToolbarVisibility={resetToolbarVisibility}
          legacyI18n={i18n}
          i18n={i18n}
          permissions={permissions}
          {...filterConfig}
        />
      </div>);
  }, [filterService, filterConfig, i18n, permissions, resetToolbarVisibility, toolbarEditor]);

  const toolbar = (
    <React.Fragment>
      {renderToolbarContainer()}
      {renderFilterViewManager()}
    </React.Fragment>
  );

  // check if the brand list id is valid
  if (!brandListId) {
    const errorMsg = _TL_('The brand list cannot be found, please try again.');
    notificationApi.showWarning(i18n.getString(errorMsg));

    const route = getRoutePathById(siteMap, 'brand-lists');
    scope.remove('brandListId');
    return (<Redirect to={`${route}?${scope.stringify()}`} />);
  }

  return (
    <div className={classNames('brand-lists-details-grid', classes.BrandListsDetailsGrid)}>
      <h2 className="title">{i18n.getString(_TL_('List name: '))}{listName}</h2>
      <div className="description">
        {i18n.getString(_TL_('Add up to 100 brands to this list. '))}
        <HelpLink query="help:app54850">{i18n.getString(_TL_('Learn more'))}</HelpLink>
      </div>
      <MessageBar className="review-delay-message-bar">
        {i18n.getString(_TL_('It takes up to a week to review a brand request. If approved, it’ll be added to your shared library. Please note that you can only apply brand protection or brand exclusion to a brand once it’s approved.'))}
      </MessageBar>
      <ContextualDetailsList
        stickyHeader
        classes={classes}
        placeholder={i18n.getString(_TL_('No records found.'))}
        toolbar={toolbar}
      />
      <ManageBrandListPanel
        cid={cid}
        aid={aid}
        brandListId={brandListId}
        isOpen={isEditPanelOpen}
        onDismiss={onCloseEditPanel}
        onSaveCallback={reloadData}
      />
      <DeleteDialog
        title={i18n.getString(_TL_('Delete brand(s)'))}
        description={i18n.getString(_TL_('Are you sure you want to delete the selected brand(s) from the list?'))}
        open={isDeleteDialogOpen}
        deleting={deleting}
        onConfirm={handleDelete}
        onDismiss={onCloseDeleteDialog}
        error={deleteError}
      />
      <StatusCallout
        itemId={selectedItemId}
        itemStatus={selectedItemStatus}
        visible={isCalloutVisible}
        onDismiss={hideCallout}
      />
    </div>
  );
};

BrandListsDetailsGridInner.propTypes = {
  siteMap: siteMapPropType.isRequired,
  permissions: PropTypes.shape({
    IsReadOnlyUser: PropTypes.bool,
  }).isRequired,
  preferenceService: PropTypes.shape({
    findByNameAtUserLevel: PropTypes.func.isRequired,
    setAtUserLevel: PropTypes.func.isRequired,
  }).isRequired,
};

export const BrandListsDetailsGrid = withPermissions(withSiteMap(BrandListsDetailsGridInner));
